import type { DiagramDocument, DiagramPage, NodeType } from './types';
import { inferBpmnMembership } from './bpmn';

export type DiagramIssueSeverity = 'error' | 'warning' | 'info';

export interface DiagramIssue {
  id: string;
  severity: DiagramIssueSeverity;
  title: string;
  description: string;
  nodeId?: string;
  edgeId?: string;
  pageId?: string;
  pageName?: string;
  code: string;
}

const BPMN_TYPES = new Set<NodeType>(['bpmnTask', 'bpmnSubprocess', 'bpmnDataObject', 'bpmnEvent', 'bpmnBoundaryEvent', 'bpmnTextAnnotation', 'bpmnGateway', 'bpmnPool', 'bpmnLane']);
const ORG_TYPES = new Set<NodeType>(['orgPerson', 'orgDepartment', 'orgExecutive', 'orgTeam']);
const MIND_TYPES = new Set<NodeType>(['mindRoot', 'mindTopic']);
const ERD_TYPES = new Set<NodeType>(['erdEntity']);
const UML_TYPES = new Set<NodeType>(['umlClass', 'umlActor', 'umlUseCase', 'umlPackage']);
const NETWORK_TYPES = new Set<NodeType>(['networkServer', 'networkSwitch', 'networkRouter', 'networkFirewall', 'networkClient', 'networkCloud', 'networkLoadBalancer', 'networkDatabase', 'networkStorage', 'networkVpn', 'networkRack']);
const CLOUD_TYPES = new Set<NodeType>(['cloudAws','cloudAzure','cloudGcp','cloudService']);

function incomingCount(page: DiagramPage, nodeId: string): number {
  return page.edges.filter((edge) => edge.targetId === nodeId).length;
}

function outgoingCount(page: DiagramPage, nodeId: string): number {
  return page.edges.filter((edge) => edge.sourceId === nodeId).length;
}

function issue(code: string, severity: DiagramIssueSeverity, title: string, description: string, nodeId?: string, edgeId?: string): DiagramIssue {
  return { id: `${code}:${nodeId ?? edgeId ?? crypto.randomUUID()}`, code, severity, title, description, nodeId, edgeId };
}

function validateBpmn(page: DiagramPage): DiagramIssue[] {
  const nodes = page.nodes.filter((node) => BPMN_TYPES.has(node.type));
  if (nodes.length === 0) return [];
  const issues: DiagramIssue[] = [];
  const membership = inferBpmnMembership(page);
  const pools = nodes.filter((node) => node.type === 'bpmnPool');
  const lanes = nodes.filter((node) => node.type === 'bpmnLane');
  const events = nodes.filter((node) => node.type === 'bpmnEvent');
  const starts = events.filter((node) => String(node.data.eventType ?? 'start') === 'start');
  const ends = events.filter((node) => String(node.data.eventType ?? 'start') === 'end');
  if (starts.length === 0) issues.push(issue('BPMN_NO_START', 'warning', 'BPMN sem evento inicial', 'Adicione pelo menos um evento BPMN de início.'));
  if (ends.length === 0) issues.push(issue('BPMN_NO_END', 'warning', 'BPMN sem evento final', 'Adicione pelo menos um evento BPMN de fim.'));

  for (const node of nodes) {
    const incoming = page.edges.filter((edge) => edge.targetId === node.id && (edge.bpmnFlowType ?? 'sequence') === 'sequence').length;
    const outgoing = page.edges.filter((edge) => edge.sourceId === node.id && (edge.bpmnFlowType ?? 'sequence') === 'sequence').length;
    const anyConnections = page.edges.filter((edge) => edge.sourceId === node.id || edge.targetId === node.id).length;
    if (node.type === 'bpmnEvent') {
      const eventType = String(node.data.eventType ?? 'start');
      if (eventType === 'start' && incoming > 0) issues.push(issue('BPMN_START_INCOMING', 'error', 'Evento inicial recebe fluxo', 'Um evento de início normalmente não deve possuir fluxo de entrada.', node.id));
      if (eventType === 'end' && outgoing > 0) issues.push(issue('BPMN_END_OUTGOING', 'error', 'Evento final envia fluxo', 'Um evento de fim não deve possuir fluxo de saída.', node.id));
      const definition = String(node.data.eventDefinition ?? 'none');
      if (definition === 'terminate' && eventType !== 'end') issues.push(issue('BPMN_TERMINATE_NOT_END', 'warning', 'Terminação fora de evento final', 'O evento de terminação normalmente é utilizado como evento final.', node.id));
      if (definition === 'timer' && eventType === 'end') issues.push(issue('BPMN_TIMER_END', 'info', 'Temporizador como evento final', 'Eventos de temporizador são normalmente de início ou intermediários.', node.id));
    }
    if (node.type === 'bpmnBoundaryEvent') {
      const attachedToId = typeof node.data.attachedToId === 'string' ? node.data.attachedToId : '';
      const target = page.nodes.find((item) => item.id === attachedToId);
      if (!target || !['bpmnTask','bpmnSubprocess'].includes(target.type)) issues.push(issue('BPMN_BOUNDARY_ATTACHMENT', 'error', 'Evento de borda sem atividade válida', 'Vincule o evento de borda a uma tarefa ou subprocesso BPMN.', node.id));
      if (incoming > 0) issues.push(issue('BPMN_BOUNDARY_INCOMING', 'warning', 'Evento de borda recebe fluxo', 'Boundary events não devem receber fluxo de sequência.', node.id));
      const definition = String(node.data.eventDefinition ?? 'timer');
      if (!['message','timer','error','signal','conditional','cancel','compensation'].includes(definition)) issues.push(issue('BPMN_BOUNDARY_DEFINITION', 'warning', 'Definição de boundary event incomum', 'Escolha uma definição suportada para manter interoperabilidade BPMN.', node.id));
    }
    if (node.type === 'bpmnTextAnnotation' && anyConnections === 0) issues.push(issue('BPMN_ANNOTATION_UNUSED', 'info', 'Anotação BPMN sem associação', 'Use uma associação para indicar a qual elemento esta anotação se refere.', node.id));
    if (node.type === 'bpmnGateway' && outgoing < 2) issues.push(issue('BPMN_GATEWAY_BRANCH', 'warning', 'Gateway sem ramificação', 'O gateway possui menos de duas saídas; verifique se a decisão está completa.', node.id));
    if (node.type === 'bpmnGateway' && String(node.data.gatewayType ?? 'exclusive') === 'eventBased' && outgoing > 0) {
      const invalid = page.edges.filter((edge) => edge.sourceId === node.id).some((edge) => {
        const target = page.nodes.find((item) => item.id === edge.targetId);
        return target && !['bpmnTask','bpmnSubprocess','bpmnEvent'].includes(target.type);
      });
      if (invalid) issues.push(issue('BPMN_EVENT_GATEWAY_TARGET', 'warning', 'Gateway baseado em eventos com destino incomum', 'Saídas de um gateway baseado em eventos devem conduzir a eventos ou atividades de recebimento.', node.id));
    }
    if ((node.type === 'bpmnTask' || node.type === 'bpmnSubprocess') && incoming === 0 && outgoing === 0) issues.push(issue('BPMN_TASK_ISOLATED', 'warning', 'Tarefa isolada', 'A tarefa BPMN não está conectada ao restante do processo.', node.id));
    if (['bpmnTask','bpmnSubprocess','bpmnEvent','bpmnBoundaryEvent','bpmnGateway'].includes(node.type) && lanes.length > 0 && !membership.nodeToLane.has(node.id)) issues.push(issue('BPMN_OUTSIDE_LANE', 'info', 'Elemento fora de uma raia', 'Há raias nesta página, mas este elemento BPMN não pertence a nenhuma delas. Mova-o para uma raia ou sincronize os vínculos BPMN.', node.id));
    if (node.type === 'bpmnLane' && !membership.laneToPool.has(node.id)) issues.push(issue('BPMN_LANE_WITHOUT_POOL', 'warning', 'Raia sem pool', 'Esta raia não está vinculada a um participante/pool BPMN.', node.id));
    if (node.type === 'bpmnDataObject' && anyConnections === 0) issues.push(issue('BPMN_DATA_UNUSED', 'info', 'Objeto de dados sem associação', 'Conecte este objeto de dados a uma atividade usando uma associação para indicar onde a informação é usada.', node.id));
  }
  for (const pool of pools) if (![...membership.laneToPool.values()].includes(pool.id)) issues.push(issue('BPMN_POOL_WITHOUT_LANE', 'info', 'Pool sem raias', 'Este participante não possui nenhuma raia associada.', pool.id));
  for (const edge of page.edges) {
    const sourcePool = membership.nodeToPool.get(edge.sourceId); const targetPool = membership.nodeToPool.get(edge.targetId);
    if (!sourcePool || !targetPool) continue;
    const flowType = edge.bpmnFlowType ?? 'sequence';
    if (sourcePool !== targetPool && flowType === 'sequence') issues.push(issue('BPMN_SEQUENCE_CROSS_POOL', 'warning', 'Fluxo de sequência cruza participantes', 'Entre pools diferentes, prefira um fluxo de mensagem em vez de fluxo de sequência.', undefined, edge.id));
    if (sourcePool === targetPool && flowType === 'message') issues.push(issue('BPMN_MESSAGE_SAME_POOL', 'info', 'Fluxo de mensagem dentro do mesmo participante', 'Fluxos de mensagem normalmente representam comunicação entre participantes distintos.', undefined, edge.id));
  }
  return issues;
}

function validateOrg(page: DiagramPage): DiagramIssue[] {
  const nodes = page.nodes.filter((node) => ORG_TYPES.has(node.type));
  if (nodes.length === 0) return [];
  const nodeIds = new Set(nodes.map((node) => node.id));
  const edges = page.edges.filter((edge) => nodeIds.has(edge.sourceId) && nodeIds.has(edge.targetId));
  const issues: DiagramIssue[] = [];
  const incoming = new Map(nodes.map((node) => [node.id, 0]));
  const outgoing = new Map(nodes.map((node) => [node.id, [] as string[]]));
  for (const edge of edges) {
    incoming.set(edge.targetId, (incoming.get(edge.targetId) ?? 0) + 1);
    outgoing.get(edge.sourceId)?.push(edge.targetId);
  }
  for (const node of nodes) {
    const managers = incoming.get(node.id) ?? 0;
    if (managers > 1) issues.push(issue('ORG_MULTI_MANAGER', 'warning', 'Mais de um superior direto', 'Este item recebe mais de uma conexão hierárquica. Verifique a estrutura de reporte.', node.id));
  }
  const roots = nodes.filter((node) => (incoming.get(node.id) ?? 0) === 0);
  if (roots.length === 0) issues.push(issue('ORG_NO_ROOT', 'error', 'Organograma sem raiz', 'Nenhum item foi identificado como topo da hierarquia. Isso pode indicar um ciclo.'));
  else if (roots.length > 1) issues.push(issue('ORG_MULTI_ROOT', 'info', 'Múltiplas raízes no organograma', `Foram encontrados ${roots.length} itens sem superior direto. Isso pode ser intencional em estruturas paralelas.`));

  const visiting = new Set<string>();
  const visited = new Set<string>();
  let cycleNode: string | null = null;
  function visit(id: string): boolean {
    if (visiting.has(id)) { cycleNode = id; return true; }
    if (visited.has(id)) return false;
    visiting.add(id);
    for (const target of outgoing.get(id) ?? []) if (visit(target)) return true;
    visiting.delete(id);
    visited.add(id);
    return false;
  }
  for (const node of nodes) if (visit(node.id)) break;
  if (cycleNode) issues.push(issue('ORG_CYCLE', 'error', 'Ciclo hierárquico detectado', 'A hierarquia retorna a um item anterior. Organogramas devem formar uma estrutura sem ciclos.', cycleNode));
  return issues;
}

function validateMindMap(page: DiagramPage): DiagramIssue[] {
  const nodes = page.nodes.filter((node) => MIND_TYPES.has(node.type));
  if (nodes.length === 0) return [];
  const nodeIds = new Set(nodes.map((node) => node.id));
  const edges = page.edges.filter((edge) => nodeIds.has(edge.sourceId) && nodeIds.has(edge.targetId));
  const issues: DiagramIssue[] = [];
  const rootsByType = nodes.filter((node) => node.type === 'mindRoot');
  if (rootsByType.length === 0) issues.push(issue('MIND_NO_ROOT', 'warning', 'Mapa mental sem tema central', 'Adicione um objeto “Tema central” para definir a raiz do mapa.'));
  if (rootsByType.length > 1) issues.push(issue('MIND_MULTI_ROOT_TYPE', 'info', 'Mais de um tema central', `Há ${rootsByType.length} temas centrais. Se forem mapas independentes, considere páginas separadas.`));
  const incoming = new Map(nodes.map((node) => [node.id, 0]));
  const outgoing = new Map(nodes.map((node) => [node.id, [] as string[]]));
  for (const edge of edges) {
    incoming.set(edge.targetId, (incoming.get(edge.targetId) ?? 0) + 1);
    outgoing.get(edge.sourceId)?.push(edge.targetId);
  }
  for (const node of nodes) {
    const count = incoming.get(node.id) ?? 0;
    if (node.type === 'mindRoot' && count > 0) issues.push(issue('MIND_ROOT_INCOMING', 'warning', 'Tema central recebe ramo', 'O tema central normalmente inicia o mapa e não deve ser filho de outro tópico.', node.id));
    if (node.type === 'mindTopic' && count > 1) issues.push(issue('MIND_MULTI_PARENT', 'warning', 'Tópico com mais de um pai', 'Um tópico de mapa mental normalmente pertence a um único ramo.', node.id));
  }
  const visiting = new Set<string>();
  const visited = new Set<string>();
  let cycleNode: string | null = null;
  function visit(id: string): boolean {
    if (visiting.has(id)) { cycleNode = id; return true; }
    if (visited.has(id)) return false;
    visiting.add(id);
    for (const target of outgoing.get(id) ?? []) if (visit(target)) return true;
    visiting.delete(id); visited.add(id); return false;
  }
  for (const node of nodes) if (visit(node.id)) break;
  if (cycleNode) issues.push(issue('MIND_CYCLE', 'error', 'Ciclo no mapa mental', 'Um ramo retorna a um tópico anterior. Remova a conexão circular para manter a hierarquia.', cycleNode));
  return issues;
}

function validateErd(page: DiagramPage): DiagramIssue[] {
  const entities = page.nodes.filter((node) => ERD_TYPES.has(node.type));
  if (entities.length === 0) return [];
  const ids = new Set(entities.map((node) => node.id));
  const issues: DiagramIssue[] = [];
  for (const entity of entities) {
    const fields = entity.entityFields ?? [];
    if (fields.length === 0) issues.push(issue('ERD_NO_FIELDS', 'warning', 'Entidade sem campos', 'Adicione ao menos um atributo à entidade.', entity.id));
    if (fields.length > 0 && !fields.some((field) => field.primaryKey)) issues.push(issue('ERD_NO_PK', 'info', 'Entidade sem chave primária', 'Nenhum campo foi marcado como chave primária.', entity.id));
    const names = new Set<string>();
    for (const field of fields) {
      const key = field.name.trim().toLocaleLowerCase('pt-BR');
      if (names.has(key)) issues.push(issue('ERD_DUPLICATE_FIELD', 'warning', 'Campo duplicado', `O campo “${field.name}” aparece mais de uma vez nesta entidade.`, entity.id));
      names.add(key);
    }
  }
  for (const edge of page.edges.filter((item) => ids.has(item.sourceId) && ids.has(item.targetId))) {
    if (!edge.sourceCardinality || !edge.targetCardinality) issues.push(issue('ERD_NO_CARDINALITY', 'info', 'Relacionamento sem cardinalidade', 'Defina a cardinalidade de origem e destino para documentar a relação.', undefined, edge.id));
  }
  return issues;
}

function validateUml(page: DiagramPage): DiagramIssue[] {
  const nodes = page.nodes.filter((node) => UML_TYPES.has(node.type));
  if (nodes.length === 0) return [];
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const issues: DiagramIssue[] = [];
  const inheritance = page.edges.filter((edge) => edge.relationshipType === 'inheritance' && nodeById.has(edge.sourceId) && nodeById.has(edge.targetId));
  const outgoingInheritance = new Map(nodes.map((node) => [node.id, [] as string[]]));
  inheritance.forEach((edge) => outgoingInheritance.get(edge.sourceId)?.push(edge.targetId));
  const visiting = new Set<string>(); const visited = new Set<string>(); let cycle: string | null = null;
  function visit(id: string): boolean { if (visiting.has(id)) { cycle = id; return true; } if (visited.has(id)) return false; visiting.add(id); for (const target of outgoingInheritance.get(id) ?? []) if (visit(target)) return true; visiting.delete(id); visited.add(id); return false; }
  for (const node of nodes) if (visit(node.id)) break;
  if (cycle) issues.push(issue('UML_INHERITANCE_CYCLE', 'error', 'Ciclo de herança UML', 'A cadeia de generalização retorna a uma classe anterior.', cycle));
  for (const edge of page.edges) {
    if (!edge.relationshipType || !nodeById.has(edge.sourceId) || !nodeById.has(edge.targetId)) continue;
    const source = nodeById.get(edge.sourceId)!; const target = nodeById.get(edge.targetId)!;
    if (['include', 'extend'].includes(edge.relationshipType) && (source.type !== 'umlUseCase' || target.type !== 'umlUseCase')) issues.push(issue('UML_USECASE_RELATION', 'warning', 'Relação de caso de uso incompatível', '«include» e «extend» devem conectar casos de uso.', undefined, edge.id));
    if (edge.relationshipType === 'inheritance' && (source.type !== 'umlClass' || target.type !== 'umlClass')) issues.push(issue('UML_INHERITANCE_TYPE', 'warning', 'Herança fora de classes', 'A generalização desta versão deve conectar classes UML.', undefined, edge.id));
  }
  return issues;
}

function validateNetwork(page: DiagramPage): DiagramIssue[] {
  const nodes = page.nodes.filter((node) => NETWORK_TYPES.has(node.type));
  if (nodes.length === 0) return [];
  const issues: DiagramIssue[] = [];
  const ips = new Map<string, string[]>();
  for (const node of nodes) {
    const ip = String(node.data.ip ?? '').trim();
    if (ip) {
      const list = ips.get(ip) ?? [];
      list.push(node.id);
      ips.set(ip, list);
      const v4 = ip.split('.');
      const looksV4 = v4.length === 4 && v4.every((part) => /^\d{1,3}$/.test(part) && Number(part) >= 0 && Number(part) <= 255);
      const looksV6 = ip.includes(':') && /^[0-9a-fA-F:]+$/.test(ip);
      if (!looksV4 && !looksV6) issues.push(issue('NETWORK_IP_FORMAT', 'info', 'Endereço IP incomum', `O valor “${ip}” não se parece com um IPv4 ou IPv6 válido.`, node.id));
    }
    const status = String(node.data.status ?? 'unknown');
    if (!['unknown', 'online', 'offline', 'maintenance', 'planned'].includes(status)) issues.push(issue('NETWORK_STATUS', 'warning', 'Status de rede inválido', 'Use um dos estados suportados para manter a documentação consistente.', node.id));
    const connected = incomingCount(page, node.id) + outgoingCount(page, node.id);
    if (node.type !== 'networkCloud' && connected === 0) issues.push(issue('NETWORK_ISOLATED', 'info', 'Equipamento isolado', 'Este equipamento não possui nenhuma conexão no diagrama.', node.id));
  }
  for (const [ip, ids] of ips) {
    if (ids.length > 1) {
      for (const id of ids) issues.push(issue('NETWORK_DUPLICATE_IP', 'warning', 'IP duplicado', `O endereço ${ip} aparece em ${ids.length} equipamentos. Verifique se isso é intencional.`, id));
    }
  }
  return issues;
}

function validateCloud(page: DiagramPage): DiagramIssue[] {
  const nodes = page.nodes.filter((node) => CLOUD_TYPES.has(node.type));
  if (nodes.length === 0) return [];
  const issues: DiagramIssue[] = [];
  for (const node of nodes) {
    const service = String(node.data.service ?? '').trim();
    const region = String(node.data.region ?? '').trim();
    if (!service) issues.push(issue('CLOUD_SERVICE_EMPTY', 'info', 'Serviço cloud não informado', 'Informe o serviço/recurso para documentar melhor a arquitetura.', node.id));
    if ((node.type === 'cloudAws' || node.type === 'cloudAzure' || node.type === 'cloudGcp') && !region) issues.push(issue('CLOUD_REGION_EMPTY', 'info', 'Região cloud não informada', 'Recursos de nuvem pública normalmente devem registrar sua região.', node.id));
    if (incomingCount(page, node.id) === 0 && outgoingCount(page, node.id) === 0) issues.push(issue('CLOUD_ISOLATED', 'info', 'Recurso cloud isolado', 'Este recurso não possui dependências ou conexões documentadas.', node.id));
  }
  return issues;
}

function validateGeneral(page: DiagramPage): DiagramIssue[] {
  const issues: DiagramIssue[] = [];
  for (const node of page.nodes) {
    if (node.hidden) continue;
    const incoming = incomingCount(page, node.id);
    const outgoing = outgoingCount(page, node.id);
    if (node.type === 'diamond' && outgoing < 2) issues.push(issue('FLOW_DECISION_BRANCH', 'info', 'Decisão com uma única saída', 'Uma decisão costuma possuir duas ou mais saídas.', node.id));
    if (node.type === 'terminator' && incoming > 0 && outgoing > 0) issues.push(issue('FLOW_TERMINATOR_MIDDLE', 'info', 'Terminal no meio do fluxo', 'Este início/fim possui entrada e saída. Verifique se ele realmente deve ser um terminal.', node.id));
  }
  return issues;
}

export function validateDiagramSemantics(page: DiagramPage): DiagramIssue[] {
  return [...validateBpmn(page), ...validateOrg(page), ...validateMindMap(page), ...validateErd(page), ...validateUml(page), ...validateNetwork(page), ...validateCloud(page), ...validateGeneral(page)];
}


export function validateDocumentSemantics(document: DiagramDocument): DiagramIssue[] {
  const issues: DiagramIssue[] = [];
  const pageIds = new Set(document.pages.map((page) => page.id));
  const pageNames = new Map<string, number>();
  for (const page of document.pages) {
    const normalizedName = page.name.trim().toLocaleLowerCase('pt-BR');
    pageNames.set(normalizedName, (pageNames.get(normalizedName) ?? 0) + 1);
    issues.push(...validateDiagramSemantics(page).map((item) => ({ ...item, pageId: page.id, pageName: page.name })));
    for (const node of page.nodes) {
      const linkPageId = typeof node.data.linkPageId === 'string' ? node.data.linkPageId.trim() : '';
      if (!linkPageId) continue;
      if (!pageIds.has(linkPageId)) {
        issues.push({
          id: `PROJECT_BROKEN_PAGE_LINK:${node.id}`,
          code: 'PROJECT_BROKEN_PAGE_LINK',
          severity: 'warning',
          title: 'Link interno quebrado',
          description: 'Este objeto aponta para uma página que não existe mais no projeto.',
          nodeId: node.id,
          pageId: page.id,
          pageName: page.name,
        });
      } else if (linkPageId === page.id) {
        issues.push({
          id: `PROJECT_SELF_PAGE_LINK:${node.id}`,
          code: 'PROJECT_SELF_PAGE_LINK',
          severity: 'info',
          title: 'Link para a própria página',
          description: 'Este atalho retorna para a página em que o objeto já está.',
          nodeId: node.id,
          pageId: page.id,
          pageName: page.name,
        });
      }
    }
  }
  for (const page of document.pages) {
    const key = page.name.trim().toLocaleLowerCase('pt-BR');
    if (key && (pageNames.get(key) ?? 0) > 1) {
      issues.push({ id: `PROJECT_DUPLICATE_PAGE_NAME:${page.id}`, code: 'PROJECT_DUPLICATE_PAGE_NAME', severity: 'info', title: 'Nome de página duplicado', description: `Há mais de uma página chamada “${page.name}”. Renomear ajuda na navegação e nos links internos.`, pageId: page.id, pageName: page.name });
    }
  }
  return issues;
}
